import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Badge, Button, Modal, ModalFooter } from '@/components/common';
import { useImportMappings } from '@/hooks/useEligibility';
import { getErrorMessage } from '@/services/api';
import type { MasterMappingImportResult } from '@/services/eligibility';

/** What the platform_code column holds per master type (mirrors the backend). */
export const PLATFORM_CODE_HELP: { type: string; label: string; example: string }[] = [
  { type: 'previous_insurer', label: 'Previous Insurer', example: "the prior insurer's platform code, e.g. icici_lombard" },
  { type: 'fuel_type', label: 'Fuel Type', example: 'the fuel type code or name, e.g. petrol' },
  { type: 'state', label: 'State', example: 'the state code, e.g. MH' },
  { type: 'rto', label: 'RTO', example: 'the RTO code, e.g. MH01' },
  { type: 'city', label: 'City', example: 'the city code, or its name when it has none' },
  { type: 'pincode', label: 'Pincode', example: 'the 6-digit pincode; if two areas share it, put the area in platform_name, e.g. Shahdara, Delhi' },
  { type: 'vehicle_make', label: 'Vehicle Make', example: 'the make code or name, e.g. MARUTI' },
  { type: 'vehicle_model', label: 'Vehicle Model', example: 'the model code or name; make_code names its make, e.g. SWIFT under MARUTI' },
  { type: 'vehicle_variant', label: 'Vehicle Variant', example: 'the variant code or name; make_code and model_code name its model' },
  { type: 'vehicle_body_type', label: 'Vehicle Body Type', example: 'the body type code or name, e.g. hatchback' },
];

/** Extra columns a type's file carries (mirrors master_mapping_io.PARENT_COLUMNS). */
const PARENT_COLUMNS: Record<string, string[]> = {
  vehicle_model: ['make_code'],
  vehicle_variant: ['make_code', 'model_code'],
};

function importColumns(masterType?: string): string[] {
  return [
    'insurer_code',
    'master_type',
    ...(masterType ? PARENT_COLUMNS[masterType] || [] : []),
    'platform_code',
    'platform_name',
    'mapped_code',
    'is_active',
  ];
}

const actionBadge: Record<string, 'success' | 'info' | 'default'> = {
  create: 'success',
  update: 'info',
  unchanged: 'default',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The master type picked on the page: the file must hold only that type. */
  masterType?: string;
}

/**
 * Upload a mappings CSV/XLSX: the file is validated first as a dry run and the
 * preview shows what every row would do; nothing is written until "Import".
 */
export function MasterMappingImportModal({ isOpen, onClose, masterType }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<MasterMappingImportResult | null>(null);
  const [applied, setApplied] = useState<MasterMappingImportResult | null>(null);
  const [error, setError] = useState('');
  const importMutation = useImportMappings();

  const reset = () => {
    setFile(null);
    setPreview(null);
    setApplied(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] || null;
    setPreview(null);
    setApplied(null);
    setError('');
    setFile(picked);
    if (!picked) return;
    try {
      setPreview(await importMutation.mutateAsync({ file: picked, dryRun: true, masterType }));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setError('');
    try {
      const result = await importMutation.mutateAsync({ file, dryRun: false, masterType });
      setApplied(result);
      setPreview(null);
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} mapping(s)`);
      } else {
        toast('Nothing to import');
      }
      if (result.failed > 0) toast.error(`${result.failed} row(s) skipped with errors`);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const shown = applied || preview;
  const helpRows = masterType ? PLATFORM_CODE_HELP.filter((h) => h.type === masterType) : PLATFORM_CODE_HELP;
  const writable = preview ? preview.created + preview.updated : 0;

  return (
    <Modal isOpen={isOpen} onClose={close} title="Import Master Mappings" size="xl">
      <div className="space-y-4">
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <p>
            Upload a CSV or Excel file with the columns{' '}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{importColumns(masterType).join(', ')}</code>.
            Download the template for the exact layout. <em>insurer_code</em> is the insurer&apos;s platform code,{' '}
            <em>mapped_code</em> is that insurer&apos;s own code for the record, <em>platform_name</em> is ignored (except to pick between areas that share a pincode), and a blank{' '}
            <em>is_active</em> means true. Existing mappings are updated in place.
          </p>
          {masterType && (
            <p data-testid="import-type-note">
              Importing <strong>{PLATFORM_CODE_HELP.find((h) => h.type === masterType)?.label || masterType}</strong> mappings:
              rows of any other master type are refused, and a blank <em>master_type</em> is taken as this one.
            </p>
          )}
          <table className="min-w-full text-xs border border-gray-200 dark:border-gray-700 rounded">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-2 py-1 text-left font-semibold text-gray-600 dark:text-gray-300">master_type</th>
                <th className="px-2 py-1 text-left font-semibold text-gray-600 dark:text-gray-300">platform_code is</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {helpRows.map((h) => (
                <tr key={h.type}>
                  <td className="px-2 py-1 font-mono text-gray-800 dark:text-gray-200">{h.type}</td>
                  <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{h.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <label htmlFor="master-mapping-file" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Mappings file
          </label>
          <input
            id="master-mapping-file"
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFile}
            className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 dark:file:bg-gray-700 file:px-3 file:py-2 file:text-sm file:text-gray-700 dark:file:text-gray-200"
          />
        </div>

        {importMutation.isPending && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{preview ? 'Importing…' : 'Checking the file…'}</p>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {shown && (
          <div className="space-y-3" data-testid="import-summary">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {shown.dry_run
                ? `Preview of ${shown.total} row(s) — nothing has been saved yet.`
                : `Imported ${shown.imported} of ${shown.total} row(s).`}
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-green-700 dark:text-green-400">{shown.dry_run ? 'Will create' : 'Created'}: {shown.created}</span>
              <span className="text-blue-700 dark:text-blue-400">{shown.dry_run ? 'Will update' : 'Updated'}: {shown.updated}</span>
              <span className="text-gray-500 dark:text-gray-400">Unchanged: {shown.unchanged}</span>
              <span className="text-red-600 dark:text-red-400">Errors: {shown.failed}</span>
            </div>

            {shown.errors.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-red-200 dark:border-red-900">
                <table className="min-w-full text-sm">
                  <thead className="bg-red-50 dark:bg-red-900/20">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-red-700 dark:text-red-300">Row</th>
                      <th className="px-3 py-2 text-left font-semibold text-red-700 dark:text-red-300">Problem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100 dark:divide-red-900">
                    {shown.errors.map((err) => (
                      <tr key={err.row}>
                        <td className="px-3 py-2 align-top font-medium text-gray-900 dark:text-white">{err.row}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400">
                          {Object.entries(err.errors).map(([field, msgs]) => (
                            <div key={field}>
                              <span className="font-medium">{field}:</span>{' '}
                              {Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {shown.rows.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      {['Row', 'Insurer', 'Type', 'Platform record', 'Insurer code', 'Active', 'Action'].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {shown.rows.map((r) => (
                      <tr key={r.row}>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{r.row}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{r.insurer_name}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.master_type}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                          {r.platform_name} <span className="font-mono text-xs text-gray-500 dark:text-gray-400">({r.platform_code})</span>
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300">{r.mapped_code}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.is_active ? 'Yes' : 'No'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={actionBadge[r.action] || 'default'}>{r.action}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={close}>
          {applied ? 'Close' : 'Cancel'}
        </Button>
        {!applied && (
          <Button
            variant="primary"
            onClick={handleImport}
            isLoading={importMutation.isPending && !!preview}
            disabled={!preview || writable === 0 || importMutation.isPending}
          >
            {preview && writable > 0 ? `Import ${writable} row(s)` : 'Import'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default MasterMappingImportModal;
